import React, { useContext, useEffect, useRef } from "react";
import { observer } from "mobx-react";
import { AppWorkspaceContext } from "../stores/AppWorkspace";
import { startAccountsActionCable, startOrderActionCable } from "../helpers/actionCable";
import { APP_PAGES } from "../helpers/constants";
import { Spinner } from "../../ui";
import AppHeader from "./AppHeader";
import AppFooter from "./AppFooter";
import SwapsPage from "./pages/SwapsPage";
import NewSwapPage from "./pages/NewSwapPage";
import FindSwapPage from "./pages/FindSwapPage";
import ApiTokensPage from "./pages/ApiTokensPage";
import SettingsPage from "./pages/SettingsPage";

const PAGE_TITLES: Record<string, string> = {
  [APP_PAGES.SWAPS]:      "Swaps",
  [APP_PAGES.NEW_SWAP]:   "New swap",
  [APP_PAGES.FIND_SWAP]:  "Find swap",
  [APP_PAGES.API_TOKENS]: "API keys",
  [APP_PAGES.SETTINGS]:   "Settings"
};

const AppWorkspaceContainer: React.FC = observer(() => {
  const workspace = useContext(AppWorkspaceContext);
  // The server's SEO title for this URL (home or a coin page). Search engines
  // index the rendered title, so the new-swap form keeps it.
  const serverTitle = useRef(document.title);

  useEffect(() => {
    workspace.hydrateDataFromPage();

    // A guest has no account channel to listen on; their live updates come from
    // the channel for the one swap they hold a token for.
    if (workspace.isGuest) {
      if (workspace.publicToken) startOrderActionCable(workspace, workspace.publicToken);
    } else {
      startAccountsActionCable(workspace);
    }
  }, [workspace]);

  useEffect(() => {
    if (workspace.openPage === APP_PAGES.NEW_SWAP && workspace.landing) {
      document.title = serverTitle.current;
      return;
    }
    const label = PAGE_TITLES[workspace.openPage] ?? "App";
    document.title = `${label} — Lightning Swap`;
  }, [workspace.openPage]);

  if (!workspace.hydrated) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col">
      <AppHeader />
      <main className="mx-auto w-full max-w-5xl flex-1 px-4 py-8 sm:px-6">
        {/* Swap history needs an account with an API key; canOpen mirrors the
            server rule, which is what actually withholds the data. */}
        {workspace.canOpen(APP_PAGES.SWAPS) &&
          workspace.openPage === APP_PAGES.SWAPS && <SwapsPage />}
        {workspace.openPage === APP_PAGES.NEW_SWAP  && <NewSwapPage />}
        {workspace.openPage === APP_PAGES.FIND_SWAP && <FindSwapPage />}
        {/* Account-only: these are the reason to sign in at all. */}
        {!workspace.isGuest && workspace.openPage === APP_PAGES.API_TOKENS && <ApiTokensPage />}
        {!workspace.isGuest && workspace.openPage === APP_PAGES.SETTINGS   && <SettingsPage />}
      </main>
      <AppFooter />
    </div>
  );
});

export default AppWorkspaceContainer;
